import { Injectable } from '@angular/core';
import { NavigationService } from './navigation.service';
import { DateFilterService } from './date-filter.service';
import { BusinessService } from "./business.service";
import { distinctUntilChanged, filter, map, pairwise, shareReplay, startWith, switchMap, take, tap } from "rxjs/operators";
import { BehaviorSubject, combineLatest, of } from "rxjs";
import { ViewAsService } from "./view-as.service";
import { initPaymentsAll, initPaymentsPaid, initPaymentsPending } from "../helpers/reports/payments";
import { SupabaseService } from "./supabase.service";
import { Page, Tab } from "../models/navigation.model";
import { commissionsPaidPaid, commissionsPendingPending, initCommissionsPaid, initCommissionsPending } from "../helpers/reports/commissions";
import { initTechPartsPaid, initTechPartsPending } from "../helpers/reports/tech-parts";
import { initOverlook } from "../helpers/reports/overlook";
import { initCompare } from "../helpers/reports/compare";
import { BusinessProfile } from "../../../../common/src/lib/models/business-profile.model";
import { initTipsPaid, initTipsPending } from '../helpers/reports/tips';
import {
  initCreditCardSettled,
  initCreditCardPending,
  initCreditPreChargebacks,
  initCreditCardRefunds,
  initCreditCardSurcharge,
  initCreditCardChargebacks
} from '../helpers/reports/credit-card';
import { TipsReportItem } from '../models/reports.model';
import { UsersService } from './users.service';
import { Router } from '@angular/router';

type ReportType = 'payments' | 'commissions' | 'techParts' | 'overlook' | 'compare' | 'tips';
type ReportTab = 'all' | 'pending' | 'paid';
export type ReportRange = {
  from: string;
  to: string;
};
type ReportRequest = {
  whichType: ReportType,
  whichTab?: ReportTab,
  businessId: string,
  params: {
    range?: ReportRange,
    compareRanges?: {
      left: ReportRange,
      right: ReportRange
    };
    users?: string[],
    offset?: number
  },
  extend?: boolean
};

export function workflowChangesHandler(changes: any, items: any) {
  if (!(changes.table === 'workflow' && changes.eventType === 'UPDATE'))
    return null;
  const data = changes.new;
  const item = items.find((item: any) => data.wId === item.id);
  if (item)
    item.jobType = data.jobType;
  return items;
}

const dateFormat = 'yyyy-MM-dd HH:mm:ss';

@Injectable({
  providedIn: 'root'
})
export class ReportsService extends DateFilterService {

  constructor(
    private supabaseService: SupabaseService,
    private businessService: BusinessService,
    private viewAsService: ViewAsService,
    private navigationService: NavigationService,
    private usersService: UsersService,
    private router: Router
  ) {
    super();
  }

  loading$ = new BehaviorSubject<boolean>(false);

  page$ = this.navigationService.page$;
  tab$ = this.navigationService.tab$;

  selectedPaymentIds = new Set<number>();
  selectedCommissionIds = new Set<number>();
  selectedTechPartIds = new Set<number>();
  selectedTips = new Set<TipsReportItem>();

  downloading$ = new BehaviorSubject<boolean>(false);

  showCommissions$ = combineLatest([
    this.businessService.selectedBusiness$,
    this.usersService.currentUser$
  ]).pipe(
    map(([business, user]) => {
      if (!business || !user)
        return of(false);
      if (user.role !== 'user')
        return of(true);
      if (user.role === 'user' && (user.jobCommissionValue || user.salesCommissionValue))
        return of(true);
      return combineLatest([
        commissionsPaidPaid(this.supabaseService, business.businessId, [user.id], business.createdAt, new Date(), 'show_commissions_paid'),
        commissionsPendingPending(this.supabaseService, business.businessId, [user.id], business.createdAt, new Date(), 'show_commissions_pending'),
      ]).pipe(
        map(([paid, pending]) => !!(paid[0].totalPaid || pending))
      );
    }),
    switchMap(show => show),
    startWith(false),
    pairwise(),
    switchMap(async ([prev, cur]) => {
      const page = await this.page$.pipe(take(1)).toPromise();
      if (!cur && prev && page === 'Commissions')
        this.router.navigate(['lobby']);
      return cur;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  report$ = combineLatest([
    this.page$,
    this.tab$,
    this.businessService.selectedBusiness$,
    this.viewAsService.selectedUsersIds$,
    this.selectedDateRangeForEvent$,
    this.selectedDateRangeForEventCompareLeft$,
    this.selectedDateRangeForEventCompareRight$
  ]).pipe(
    map(data => {
      this.loading$.next(true);
      return {
        page: data[0],
        tab: data[1],
        business: data[2],
        users: data[3],
        singleDate: !data[4] || !((data[4] as any[])[0]) || !((data[4] as any[])[1])
          ? {
            start: (data[2] as BusinessProfile).createdAt,
            end: new Date()
          }
          : {
            start: (data[4] as any[])[0],
            end: (data[4] as any[])[1]
          },
        leftDate: {
          start: data[5] ? (data[5] as any[])[0] : undefined,
          end: data[5] ? (data[5] as any[])[1] : undefined
        },
        rightDate: {
          start: data[6] ? (data[6] as any[])[0] : undefined,
          end: data[6] ? (data[6] as any[])[1] : undefined
        }
      };
    }),
    filter(data => {
      if (data.page === 'Compare')
        return !!data.page && !!data.business;

      return !!data.page && !!data.business && !!data.singleDate.start && !!data.singleDate.end;
    }),
    distinctUntilChanged((prev, cur) => {
      if (cur.page !== 'Compare')
        return false;
      return prev.rightDate.start?.getTime() === cur.rightDate.start?.getTime() && prev.leftDate.start?.getTime() === cur.leftDate.start?.getTime() &&
        prev.rightDate.end?.getTime() === cur.rightDate.end?.getTime() && prev.leftDate.end?.getTime() === cur.leftDate.end?.getTime();
    }),
    switchMap(data => this.reportInitObservable(
        data.page as Page,
        data.tab as Tab,
        (data.business as BusinessProfile).businessId,
        data.users as (number[] | null),
        data.page !== 'Compare' ? data.singleDate.start : undefined,
        data.page !== 'Compare' ? data.singleDate.end : undefined,
        data.page === 'Compare' ? data.leftDate.start : undefined,
        data.page === 'Compare' ? data.leftDate.end : undefined,
        data.page === 'Compare' ? data.rightDate.start : undefined,
        data.page === 'Compare' ? data.rightDate.end : undefined
    )!),
    tap(_ => this.loading$.next(false)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  reportInitObservable(
      page: Page,
      tab: Tab,
      businessId: string,
      selectedUsers: number[] | null,
      startTime?: Date,
      endTime?: Date,
      leftStartTime?: Date,
      leftEndTime?: Date,
      rightStartTime?: Date,
      rightEndTime?: Date,
  ) {
    switch (page) {
      case 'Transactions':
        switch (tab) {
          case 'All':
            return initPaymentsAll(
                this.supabaseService,
                businessId,
                selectedUsers,
                startTime!,
                endTime!
            );
          case 'Paid':
            return initPaymentsPaid(
                this.supabaseService,
                businessId,
                selectedUsers,
                startTime!,
                endTime!
            );
          case 'Pending':
            return initPaymentsPending(
                this.supabaseService,
                businessId,
                selectedUsers,
                startTime!,
                endTime!
            );
          default:
            return null;
        }
      case 'Commissions':
        switch (tab) {
          case 'Paid':
            return initCommissionsPaid(
                this.supabaseService,
                businessId,
                selectedUsers,
                startTime!,
                endTime!
            );
          case 'Pending':
            return initCommissionsPending(
                this.supabaseService,
                businessId,
                selectedUsers,
                startTime!,
                endTime!
            );
          default:
            return null;
        }
      case 'Tech Parts':
        switch (tab) {
          case 'Paid':
            return initTechPartsPaid(
                this.supabaseService,
                businessId,
                selectedUsers,
                startTime!,
                endTime!
            );
          case 'Pending':
            return initTechPartsPending(
                this.supabaseService,
                businessId,
                selectedUsers,
                startTime!,
                endTime!
            );
          default:
            return null;
        }
      case 'Tips':
        switch (tab) {
          case 'Paid':
            return initTipsPaid(
                this.supabaseService,
                businessId,
                selectedUsers,
                startTime!,
                endTime!
            );
          case 'Pending':
            return initTipsPending(
                this.supabaseService,
                businessId,
                selectedUsers,
                startTime!,
                endTime!
            );
          default:
            return null;
        }
      case 'Credit Card':
        switch (tab) {
          case 'Settled':
            return initCreditCardSettled(
                this.supabaseService,
                businessId,
                selectedUsers,
                startTime!,
                endTime!
            );
          case 'Pending':
            return initCreditCardPending(
                this.supabaseService,
                businessId,
                selectedUsers,
                startTime!,
                endTime!
            );
          case 'Pre-chargebacks':
            return initCreditPreChargebacks(
                this.supabaseService,
                businessId,
                selectedUsers,
                startTime!,
                endTime!
            );
          case 'Chargebacks':
            return initCreditCardChargebacks(
                this.supabaseService,
                businessId,
                selectedUsers,
                startTime!,
                endTime!
            );
          case 'Refunds':
            return initCreditCardRefunds(
                this.supabaseService,
                businessId,
                selectedUsers,
                startTime!,
                endTime!
            );
          case 'Surcharge':
            return initCreditCardSurcharge(
                this.supabaseService,
                businessId,
                selectedUsers,
                startTime!,
                endTime!
            );
          default:
            return null;
        }
      case 'Overview':
        return initOverlook(
            this.supabaseService,
            businessId,
            selectedUsers,
            startTime!,
            endTime!
        );
      case 'Compare':
        return initCompare(
            this.supabaseService,
            businessId,
            selectedUsers,
            leftStartTime,
            leftEndTime,
            rightStartTime,
            rightEndTime
        );
      default:
        return null;
    }
  }

  async exportPaymentsForDownload(data: any) {
    // const businessId = (await this.businessService.selectedBusiness$.pipe(take(1)).toPromise())?.businessId;
    // const paymentIds = ids.map(id => id.workflowId);
    //
    // if (businessId && paymentIds) {
    //   const endpoint = '/exportPayments';
    //   const headers = this.getAuthHeaders();
    //
    //   try {
    //     const res = await this.http.post(`${ apiUrl }${ endpoint }`, {
    //       paymentIds,
    //       businessId
    //     }, { headers }).pipe(take(1)).toPromise();
    //
    //     return this.fixExportedPaymentsData(res);
    //   } catch (e) {
    //     this.handleHttpError(e);
    //   }
    // }
    //
    // return {};

    return true;
  }

  async downloadExport() {
    const business = await this.businessService.selectedBusiness$.pipe(take(1)).toPromise();

    return (await this.supabaseService.rpcFunc(business!.businessId, 'export', {
      workflow_ids: Array.from(this.selectedPaymentIds)
    }));
  }

  async mark(type: 'commissions' | 'tech-parts' | 'tips', val?: boolean, ids?: (number | TipsReportItem)[]) {
    const business = await this.businessService.selectedBusiness$.pipe(take(1)).toPromise();

    if (type === 'tech-parts') {
      await this.supabaseService.rpcFunc(business!.businessId, 'mark_tech_parts', {
        in_ids: ids ?? Array.from(this.selectedTechPartIds),
        val
      });

      this.selectedTechPartIds.clear();
    }

    if (type === 'commissions') {
      await this.supabaseService.rpcFunc(business!.businessId, 'mark_commissions', {
        in_ids: Array.from(this.selectedCommissionIds)
      });

      this.selectedCommissionIds.clear();
    }

    if (type === 'tips') {
      await this.supabaseService.rpcFunc(business!.businessId, 'mark_tips_as_paid', {
        in_to_mark: ids ?? Array.from(this.selectedTips).map(tip => ({
          paymentId: tip.paymentId,
          amount: tip.amount,
          firstName: tip.uFirstName,
          lastName: tip.uLastName,
          userId: tip.userId
        }))
      });

      this.selectedTips.clear();
    }
  }

  async sumSelectedTechParts() {
    const business = await this.businessService.selectedBusiness$.pipe(take(1)).toPromise();
    const selectedIds = Array.from(this.selectedTechPartIds);

    return this.supabaseService.rpcFunc<number>(business!.businessId, 'sum_pending_tech_parts', {
      in_ids: selectedIds
    });
  }

  async sumSelectedCommissions() {
    const business = await this.businessService.selectedBusiness$.pipe(take(1)).toPromise();
    const selectedIds = Array.from(this.selectedCommissionIds);

    return this.supabaseService.rpcFunc<number>(business!.businessId, 'sum_pending_commissions', {
      in_ids: selectedIds
    });
  }

  async sumSelectedTips() {
    const business = await this.businessService.selectedBusiness$.pipe(take(1)).toPromise();
    const selectedTips = Array.from(this.selectedTips).map(tip => ({
        paymentId: tip.paymentId,
        userId: tip.userId
      })
    );

    return this.supabaseService.rpcFunc<number>(business!.businessId, 'sum_pending_tips', {
      in_to_sum: selectedTips
    });
  }

}
